import { useState, useEffect } from "react";
import { DirectionProvider } from "@base-ui/react/direction-provider";
import { I18nProvider } from "@/lib/i18n/i18n-context";
import type { SupportedLanguage } from "@/lib/i18n";
import {
  DASHBOARD_LANGUAGE_STORAGE_KEY,
  resolveStoredDashboardLanguage,
} from "@/lib/i18n/dashboard-language";

/**
 * DashboardShell — reads the admin's language preference from localStorage
 * and provides it to I18nProvider + keeps html lang/dir in sync.
 *
 * Initial lang/dir is set by an inline script in index.html (prevents flash).
 * This component keeps it in sync with React state for dynamic language switching.
 */
export function DashboardShell({ children }: { children: React.ReactNode }) {
  const [lang] = useState<SupportedLanguage>(() => {
    return resolveStoredDashboardLanguage();
  });
  const direction = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = direction;
    // Persist to cookie (used by index.html inline script on next load)
    document.cookie = `${DASHBOARD_LANGUAGE_STORAGE_KEY}=${lang};path=/;max-age=31536000;SameSite=Lax`;
  }, [direction, lang]);

  return (
    <I18nProvider language={lang}>
      <DirectionProvider direction={direction}>
        {children}
      </DirectionProvider>
    </I18nProvider>
  );
}
